import { useSyncExternalStore } from 'react';
import type { SupportSessionDescriptor } from '@/types/platform';

/**
 * The one place that knows whether a support session is open.
 *
 * WHY THIS IS NOT COMPONENT STATE
 * The banner has to be on screen on every route, including routes the support
 * screen never rendered and routes reached by a full page load. State held inside
 * SupportAccessPage disappears the moment the operator navigates away, and the
 * session does not disappear with it — that gap is precisely the "silent
 * impersonation" the whole PA-17 design exists to prevent. So the descriptor lives
 * in a module-level store that PlatformLayout subscribes to, above the router.
 *
 * WHY IT IS MIRRORED TO sessionStorage
 * A refresh must not clear the banner while the grant is still live on the server.
 * sessionStorage rather than localStorage: the grant is bounded by the tab and by
 * the clock, and a banner surviving into a different browsing session tomorrow
 * would be describing a session that ended hours ago.
 *
 * The store is not authoritative and is never trusted for authorisation. The
 * server decides what a support session may do; this only decides what is said on
 * screen while one is open. It can therefore be wrong in exactly one direction —
 * showing a banner for a session that has already expired — and that is the
 * direction the failure is allowed to go.
 */

const STORAGE_KEY = 'campusone.support-session';

type Listener = () => void;

let current: SupportSessionDescriptor | null = readStored();
const listeners = new Set<Listener>();

function readStored(): SupportSessionDescriptor | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const candidate = parsed as Partial<SupportSessionDescriptor>;
    if (typeof candidate.grantId !== 'string' || typeof candidate.expiresAt !== 'string') return null;
    if (candidate.mode !== 'READ_ONLY' && candidate.mode !== 'IMPERSONATION') return null;
    return {
      grantId: candidate.grantId,
      mode: candidate.mode,
      institutionId: typeof candidate.institutionId === 'string' ? candidate.institutionId : '',
      targetPersonId: typeof candidate.targetPersonId === 'string' ? candidate.targetPersonId : undefined,
      // Anything other than an explicit false means read-only. A corrupted value
      // must not be the thing that grants write access in the interface.
      readOnly: candidate.readOnly !== false,
      // Never restored as false, whatever the stored value says.
      impersonationBanner: true,
      expiresAt: candidate.expiresAt,
      startedAt: typeof candidate.startedAt === 'string' ? candidate.startedAt : candidate.expiresAt,
    };
  } catch {
    // A private-mode browser that throws on sessionStorage must not take the
    // console down; it just means the banner does not survive a refresh.
    return null;
  }
}

function emit(): void {
  for (const listener of listeners) listener();
}

export function setSupportSession(session: SupportSessionDescriptor | null): void {
  current = session ? { ...session, impersonationBanner: true } : null;
  try {
    if (current) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage being unavailable changes nothing about the in-memory banner.
  }
  emit();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): SupportSessionDescriptor | null {
  return current;
}

export function useSupportSession(): SupportSessionDescriptor | null {
  // The server snapshot is the same value: there is no support session during SSR
  // and there is no SSR here, but useSyncExternalStore requires the argument.
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** True once the grant's own expiry has passed, whatever the store still holds. */
export function supportSessionExpired(session: SupportSessionDescriptor): boolean {
  const expiry = new Date(session.expiresAt).getTime();
  return Number.isFinite(expiry) && expiry <= Date.now();
}
